/**
 * ISSN format and check-digit validation (ISO 3297).
 *
 * An ISSN is seven digits and a check character, written as two groups of four:
 * 0378-5955. Weight the seven digits 8, 7, 6, 5, 4, 3, 2, add the products, and the
 * check character is 11 minus the remainder mod 11 — written X when it is 10 and 0
 * when it is 11. The arithmetic is the ISBN-10 rule with one digit fewer.
 *
 * A valid check digit proves the number is well formed, not that it was ever
 * assigned: only the ISSN Portal can say which serial an ISSN belongs to.
 */

import type { CheckDigitWorking } from "@/lib/tool-kit/identifiers/isbn";

export type IssnProblem =
  | "length"
  | "characters"
  | "x-position"
  | "check-digit";

export interface IssnAnalysis {
  /** The ISSN as found in the text. */
  input: string;
  /** Eight characters, no hyphen, X upper-cased. */
  compact: string;
  /** NNNN-NNNC. */
  formatted: string;
  valid: boolean;
  problem?: IssnProblem;
  expectedCheck?: string;
  working?: CheckDigitWorking;
}

const ISSN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2];

const RE_ISSN_TOKEN = /\b\d{4}[\s\u2010-\u2015-]?\d{3}[\dXx](?![\dXx])/g;
const RE_SEPARATORS = /[\s\u2010-\u2015-]/g;
const RE_LABEL = /^\s*(?:e-?|p-?|print\s+|online\s+)?issn(?:-l)?\s*[:#]?\s*/i;
const RE_SEVEN_DIGITS = /^\d{7}$/;
const RE_CHECK_CHAR = /^[\dX]$/;

/** The working for the check character of the seven digits given. */
export function issnWorking(firstSeven: string): CheckDigitWorking {
  const digits = [...firstSeven].map(Number);
  const products = digits.map((digit, index) => digit * ISSN_WEIGHTS[index]);
  const sum = products.reduce((total, value) => total + value, 0);
  const remainder = sum % 11;
  const raw = (11 - remainder) % 11;
  return {
    digits,
    weights: [...ISSN_WEIGHTS],
    products,
    sum,
    modulus: 11,
    remainder,
    check: raw === 10 ? "X" : String(raw),
  };
}

export function issnCheckDigit(firstSeven: string): string {
  return issnWorking(firstSeven).check;
}

function formatCompact(compact: string): string {
  return compact.length === 8
    ? `${compact.slice(0, 4)}-${compact.slice(4)}`
    : compact;
}

/** Validate one ISSN, labelled or not ("eISSN: 1476-4687", "03785955"). */
export function analyzeIssn(raw: string): IssnAnalysis {
  const input = raw.trim();
  const compact = input
    .replace(RE_LABEL, "")
    .replace(RE_SEPARATORS, "")
    .toUpperCase();
  const formatted = formatCompact(compact);
  const base = { input, compact, formatted };
  if (compact.length !== 8) {
    return { ...base, valid: false, problem: "length" };
  }
  const body = compact.slice(0, 7);
  const check = compact[7];
  if (body.includes("X")) {
    return { ...base, valid: false, problem: "x-position" };
  }
  if (!(RE_SEVEN_DIGITS.test(body) && RE_CHECK_CHAR.test(check))) {
    return { ...base, valid: false, problem: "characters" };
  }
  const working = issnWorking(body);
  if (working.check !== check) {
    return {
      ...base,
      valid: false,
      problem: "check-digit",
      expectedCheck: working.check,
      working,
    };
  }
  return { ...base, valid: true, expectedCheck: working.check, working };
}

/**
 * Every ISSN-shaped token in a block of text, in order — "ISSN 0028-0836 (print),
 * 1476-4687 (online)" yields both. A line with none is reported by the caller.
 */
export function findIssnTokens(text: string): string[] {
  return [...text.matchAll(RE_ISSN_TOKEN)].map((match) => match[0]);
}
